/** @format */

import { ChangeDetectionStrategy, Component, computed, effect, inject, input } from '@angular/core';
import { MyCustomizations } from '@shared/data-access/interfaces';
import {
	selectKillswitch,
	selectResolvedSkuPartNumber,
	selectSelectedPersonalizationOptions
} from '@pdp/data-access/state';
import { Store } from '@ngrx/store';
import { AuthenticationService } from '@shared/authentication/data-access';
import { WishListService } from '@shared/favorites/data-access/services';
import { AddToCartButtonComponent } from '../add-to-cart-button/add-to-cart-button.component';
import { AddToCartAlertsComponent } from '../add-to-cart-alerts/add-to-cart-alerts.component';
import { AddToListButtonComponent } from '../add-to-list-button/add-to-list-button.component';
import { selectErrorList } from '@pdp/attributes/data-access';
import { HmfAlertBannerComponent } from '@dsg-tech/homefield-angular';

@Component({
	selector: 'pdp-feature-add-to-cart-section',
	imports: [
		AddToCartButtonComponent,
		AddToCartAlertsComponent,
		AddToListButtonComponent,
		HmfAlertBannerComponent
	],
	templateUrl: './add-to-cart-section.component.html',
	styleUrl: './add-to-cart-section.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class AddToCartSectionComponent {
	private readonly store = inject(Store);
	private readonly authService = inject(AuthenticationService);
	private readonly wishlistService = inject(WishListService);

	constructor() {
		effect(() => {
			if (this.isAuthenticated()) {
				this.wishlistService.getSavedItemsFromAllLists(this.ecode()).subscribe();
			}
		});
	}

	readonly customizedAttributes = input<MyCustomizations>(null);
	readonly ecode = input.required<string>();
	readonly isCustomizable = input<boolean>(false);
	readonly size = input<'medium' | 'large'>('large');
	readonly isSingleSkuProduct = input<boolean>(false);

	readonly favoritesEnabled = this.store.selectSignal(selectKillswitch('favoritesEnabled'));
	readonly resolvedSkuPartNumber = this.store.selectSignal(selectResolvedSkuPartNumber);
	readonly listProductPersonalization = this.store.selectSignal(
		selectSelectedPersonalizationOptions
	);
	readonly isAuthenticated = this.authService.isAuthenticatedSignal;

	readonly errorListFormatter = new Intl.ListFormat('en', {
		style: 'long',
		type: 'conjunction'
	});

	readonly attributeErrors = this.store.selectSignal(selectErrorList);
	readonly attributeErrorListString = computed(() => {
		// Remove if isAttributeErrorUpdatedEnabled is ever turned off & removed
		const errorList = this.attributeErrors();

		if (!errorList) {
			return '';
		}

		// Exclude personalization errors from the error list
		const keys = Object.keys(errorList).filter((key) => key !== 'Personalization');

		return this.errorListFormatter.format(keys);
	});
}
